import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Globe, Search, ChevronDown, Check, X } from 'lucide-react';
import apiClient from '../../services/apiClient';
import { toast } from 'react-toastify';
import { getSiteTimezone, getTimezoneListWithOffsets } from '../../utils/dateUtils';

export const TopBarTimezonePicker: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTz, setCurrentTz] = useState<string>(() => getSiteTimezone());
  const [isSaving, setIsSaving] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const timezoneList = useMemo(() => getTimezoneListWithOffsets(), []);

  const selectedItem = useMemo(() => {
    return timezoneList.find((tz) => tz.value === currentTz) || {
      value: currentTz,
      label: currentTz ? `${currentTz.replace(/_/g, ' ')}` : 'Asia/Kolkata',
      offset: '+5:30',
    };
  }, [timezoneList, currentTz]);

  const filteredTimezones = useMemo(() => {
    if (!searchTerm.trim()) return timezoneList;
    const term = searchTerm.trim().toLowerCase();
    return timezoneList.filter((tz) => tz.searchKey.includes(term));
  }, [timezoneList, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    } else {
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleSelectTimezone = async (tzValue: string) => {
    if (tzValue === currentTz) {
      setIsOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      await apiClient.post('/admin/updatewebsiteconfig', {
        websiteConfig: { site_timezone: tzValue },
      });
      if (!(window as any).adminSiteConfig) {
        (window as any).adminSiteConfig = {};
      }
      (window as any).adminSiteConfig.site_timezone = tzValue;
      localStorage.setItem('admin_site_timezone', tzValue);
      setCurrentTz(tzValue);
      toast.success(`Admin timezone changed to ${tzValue}`);
      setIsOpen(false);
      
      // Dispatch custom event and reload or trigger query invalidation
      window.dispatchEvent(new CustomEvent('siteTimezoneChanged', { detail: tzValue }));
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error('Failed to update timezone:', err);
      toast.error('Failed to update timezone');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="relative z-50" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/60 text-slate-200 hover:text-white transition-all text-xs font-medium shadow-sm"
        title="Change Admin Timezone"
      >
        <Globe size={14} className="text-emerald-400 shrink-0" />
        <span className="hidden md:inline max-w-[140px] truncate">
          {selectedItem.value.split('/')[1] || selectedItem.value}
        </span>
        {selectedItem.offset && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-slate-900 border border-slate-700 text-slate-400">
            {selectedItem.offset}
          </span>
        )}
        <ChevronDown
          size={12}
          className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div 
          className="absolute right-0 top-full mt-2 w-72 sm:w-80 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150"
          style={{
            backgroundColor: '#0b1120',
            border: '1px solid #334155',
            zIndex: 99999,
            boxShadow: '0 20px 35px -5px rgba(0, 0, 0, 0.8), 0 10px 15px -5px rgba(0, 0, 0, 0.6)'
          }}
        >
          {/* Header */}
          <div 
            className="p-3 border-b"
            style={{ backgroundColor: '#070b14', borderColor: '#1e293b' }}
          >
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
              Admin Display Timezone
            </div>
            <div className="relative flex items-center">
              <Search size={14} className="absolute left-2.5 text-slate-500" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search city, country, GMT+..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  backgroundColor: '#0f172a',
                  borderColor: '#334155',
                  color: '#ffffff'
                }}
                className="w-full rounded-lg pl-8 pr-7 py-1.5 text-xs border focus:outline-none focus:ring-1 focus:ring-emerald-500 placeholder:text-slate-500"
              />
              {searchTerm && (
                <X
                  size={12}
                  className="absolute right-2.5 text-slate-500 hover:text-white cursor-pointer"
                  onClick={() => setSearchTerm('')}
                />
              )}
            </div>
          </div>

          {/* List */}
          <div 
            className="max-h-64 overflow-y-auto p-1.5 space-y-0.5 tz-scrollbar"
            style={{ backgroundColor: '#0b1120' }}
          >
            {filteredTimezones.length > 0 ? (
              filteredTimezones.map((tz) => {
                const isSelected = tz.value === currentTz;
                return (
                  <button
                    key={tz.value}
                    type="button"
                    disabled={isSaving}
                    onClick={() => handleSelectTimezone(tz.value)}
                    style={{
                      backgroundColor: isSelected ? 'rgba(16, 185, 129, 0.15)' : 'transparent',
                    }}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between rounded-lg transition-colors ${
                      isSelected
                        ? 'text-emerald-400 font-semibold'
                        : 'text-slate-200 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      {isSelected ? (
                        <Check size={13} className="text-emerald-400 shrink-0" />
                      ) : (
                        <span className="w-3.5" />
                      )}
                      <span className="truncate">{tz.value.replace(/_/g, ' ')}</span>
                    </div>
                    {tz.offset && (
                      <span 
                        style={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#94a3b8' }}
                        className="ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded border shrink-0"
                      >
                        {tz.offset}
                      </span>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="p-4 text-center text-xs text-slate-500">
                No timezones match "{searchTerm}"
              </div>
            )}
          </div>
          <style>{`
            .tz-scrollbar::-webkit-scrollbar {
              width: 5px;
            }
            .tz-scrollbar::-webkit-scrollbar-track {
              background: #0b1120;
            }
            .tz-scrollbar::-webkit-scrollbar-thumb {
              background: #334155;
              border-radius: 4px;
            }
            .tz-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #475569;
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default TopBarTimezonePicker;
